import HBIcon from "@/components/HBIcon";
import classNames from "classnames";
import { throttle } from "lodash";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";

export default forwardRef(
  /**
   * @typedef {{
   *   className?: string;
   *   width?: string | number;
   *   buttonClass?: string;
   *   calibration?: number;
   *   children?: React.ReactNode;
   * } & import("react").HTMLAttributes<HTMLDivElement>} Props
   *
   * @param {Props} props
   */
  function Swiper(
    { className, width, buttonClass, children, calibration = 0, ...rest },
    ref
  ) {
    const scrollTarget = useRef();
    const [leftHide, setLeftHide] = useState(true);
    const [rightHide, setRightHide] = useState(true);

    const swipeLeft = useCallback(() => {
      const scrollDom = scrollTarget.current;
      const { scrollLeft, offsetWidth } = scrollDom;

      const nextLeft = scrollLeft - offsetWidth - calibration;
      scrollTarget.current.scrollTo({
        top: 0,
        left: nextLeft,
        behavior: "smooth",
      });
    }, [calibration]);

    const swipeRight = useCallback(() => {
      const scrollDom = scrollTarget.current;
      const { scrollLeft, offsetWidth } = scrollDom;

      const nextLeft = scrollLeft + offsetWidth + calibration;

      scrollTarget.current.scrollTo({
        top: 0,
        left: nextLeft,
        behavior: "smooth",
      });
    }, [calibration]);

    useEffect(() => {
      if (scrollTarget.current) {
        if (
          scrollTarget.current.offsetWidth < scrollTarget.current.scrollWidth
        ) {
          setRightHide(false);
        }

        const onScroll = throttle((e) => {
          if (
            e.target.scrollLeft + e.target.offsetWidth >=
            e.target.scrollWidth
          ) {
            setRightHide(true);
          } else {
            setRightHide(false);
          }

          if (e.target.scrollLeft > 0) {
            setLeftHide(false);
          } else {
            setLeftHide(true);
          }
        }, 300);
        scrollTarget.current.addEventListener("scroll", onScroll);

        const onWeel = throttle((event) => {
          // 禁用默认滚动行为
          event.preventDefault();

          // 获取滚轮事件的 deltaX 属性值，即滚轮水平滚动位置的变化量
          const delta = event.deltaY;

          // 根据变化量来更新页面内容的水平滚动位置
          scrollTarget.current.scrollLeft += delta;
        }, 50);

        scrollTarget.current.addEventListener("wheel", onWeel);

        return () => {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          scrollTarget.current?.removeEventListener("scroll", onScroll);
          // eslint-disable-next-line react-hooks/exhaustive-deps
          scrollTarget.current?.removeEventListener("wheel", onWeel);
        };
      }
    }, []);

    const scrollToTarget = useCallback((target) => {
      const scrollDom = scrollTarget.current;
      const { scrollLeft, offsetWidth, scrollWidth } = scrollDom;

      if (scrollLeft + offsetWidth >= scrollWidth) {
        setRightHide(true);
      } else {
        setRightHide(false);
      }

      if (scrollLeft > 0) {
        setLeftHide(false);
      } else {
        setLeftHide(true);
      }

      // 在右边
      if (target.offsetLeft + target.offsetWidth > offsetWidth + scrollLeft) {
        scrollDom.scrollTo({
          top: 0,
          left: target.offsetLeft,
          behavior: "smooth",
        });
        return;
      }

      // 在左边
      if (target.offsetLeft < scrollLeft) {
        scrollDom.scrollTo({
          top: 0,
          left: target.offsetLeft,
          behavior: "smooth",
        });
      }
    }, []);

    useImperativeHandle(ref, () => ({ scrollToTarget }));

    return (
      <div className={classNames(styles.swiperWrap)} {...rest}>
        <span
          onClick={swipeLeft}
          className={classNames(styles.leftBtn, buttonClass, styles.btn)}
          style={leftHide ? { display: "none" } : {}}
        >
          <HBIcon type="ic_chevron_left" />
        </span>
        <span
          onClick={swipeRight}
          className={classNames(styles.rightBtn, buttonClass, styles.btn)}
          style={rightHide ? { display: "none" } : {}}
        >
          <HBIcon type="ic_chevron_right" />
        </span>
        <div
          className={classNames(styles.swiperContent, className)}
          ref={scrollTarget}
          style={{ width: width }}
        >
          {children}
        </div>
      </div>
    );
  }
);
